import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { EditableTextArea, type EditableTextAreaProps } from '@pkg/components/EditableTextArea';
import { useAppDispatch, useGetGroup, useUpdateGroup } from '@pkg/hooks';
import { filterLineBreaks } from '@pkg/utils';

import { addNotification } from '@app/store/actions/notification';

import { EditingBackdrop } from '../styles';

import { StyledEditableDescription } from './styles';

export interface EditableDescriptionProps extends Pick<EditableTextAreaProps, 'readOnly'> {
    description?: EditableTextAreaProps['value'];
}

// TODO: This should be using a "unique id" hook.
const DESCRIPTION_ID = 'Pods-EditableDescription';
const DESCRIPTION_MAX_LENGTH = 90;

export function EditableDescription({ description = '', readOnly }: EditableDescriptionProps) {
    const dispatch = useAppDispatch();
    const { t: translate } = useTranslation('groups');

    const { data } = useGetGroup();
    const { mutate, isLoading } = useUpdateGroup();

    const [liveValue, setLiveValue] = useState(description);
    const [backupValue, setBackupValue] = useState<string>(description);

    const [focused, setFocused] = useState(false);

    useEffect(() => {
        setLiveValue(description);
        setBackupValue(description);
    }, [description]);

    function updateValue(newValue: string) {
        if (!data) {
            dispatch(
                addNotification({
                    message: translate('pods_missing_id_error'),
                    severity: 'error',
                }),
            );

            return;
        }

        const trimmedValue = newValue.trim();
        setLiveValue(trimmedValue);

        if (trimmedValue !== description) {
            mutate(
                {
                    id: data.id,
                    value: {
                        description: trimmedValue,
                    },
                },
                {
                    // Do we also want to dispatch a `onSuccess()` message?
                    onError({ message }) {
                        dispatch(addNotification({ message }));
                    },
                },
            );

            setBackupValue(trimmedValue);
        }
    }

    const handleChange: EditableTextAreaProps['onChange'] = ({ target }) => {
        setLiveValue(filterLineBreaks(target.value));
    };

    const handleEnter: EditableTextAreaProps['onEnter'] = (event, value) => {
        event.preventDefault();
        updateValue(value);
        setFocused(false);
    };

    function handleBlur() {
        updateValue(liveValue);
        setFocused(false);
    }

    function handleFocus() {
        setBackupValue(liveValue);
        setFocused(true);
    }

    function handleCancel() {
        const revertedValue = backupValue ? backupValue : description;
        setLiveValue(revertedValue);
    }

    return (
        <>
            <StyledEditableDescription $focused={focused}>
                <EditableTextArea
                    id={DESCRIPTION_ID}
                    value={liveValue}
                    placeholder={translate('pods_description_placeholder')}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    disabled={isLoading}
                    readOnly={readOnly}
                    onChange={handleChange}
                    onEnter={handleEnter}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    onCancel={handleCancel}
                />
            </StyledEditableDescription>

            {focused && <EditingBackdrop />}
        </>
    );
}
